// Mirror screens (2026-10-25): a manifest screen listed in the room's `mirrors` (rooms.json) shows a planar
// reflection of the room instead of a poster (the gallery_mirror room: the gallery's gold oval, facing the vanitas
// set). three's Reflector renders the scene from the mirrored camera into a texture before each frame that draws the
// mirror; a mirror out of view costs nothing (frustum culled, so it does not render). The texture is small: the oval
// covers a small part of the screen. Desktop only: phones keep the poster.

import { Mesh, Shape, ShapeGeometry, Vector3, type Object3D } from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import type { ScreenInfo } from '../scene/manifest';

/** How far the mirror reaches under the frame on each side, m. */
const OVERLAP_M = 0.02;
/** Long side of the reflection texture, pixels. */
const TEXTURE_PX = 768;

/**
 * A mirror in place of `screen` (hidden): an ellipse (`shape` other than "rect" with a rounded outline would need
 * its own geometry; the oval screen is an ellipse inside its bounding rectangle), at the screen's centre, facing its
 * normal, 2 mm in front of it.
 */
export function makeMirror(screen: Mesh, info: ScreenInfo, parent: Object3D): Reflector | null {
  if (!info.center || !info.normal || !info.width || !info.height) return null;
  // a little larger than the screen: its edge runs under the frame (the frame is 30 mm wide and stands 25 mm off the
  // panel), so no gap shows at the rim from an angle (user 2026-10-26)
  const w = info.width + 2 * OVERLAP_M, h = info.height + 2 * OVERLAP_M;
  const shape = new Shape();
  shape.absellipse(0, 0, w / 2, h / 2, 0, Math.PI * 2, false, 0);
  const geo = new ShapeGeometry(shape, 64);
  const k = TEXTURE_PX / Math.max(w, h);
  const mirror = new Reflector(geo, {
    textureWidth: Math.round(w * k),
    textureHeight: Math.round(h * k),
    color: 0xd8d4cc,           // a slightly warm, slightly dark glass, so it reads as a mirror and not a hole
    clipBias: 0.003,
  });
  const n = new Vector3(...info.normal).normalize();
  const c = new Vector3(...info.center).addScaledVector(n, 0.004);
  mirror.position.copy(c);
  mirror.lookAt(c.clone().add(n));          // local +Z (the Reflector's normal) along the screen's normal
  mirror.name = `mirror:${info.id}`;
  mirror.updateMatrixWorld(true);
  screen.visible = false;
  parent.add(mirror);
  return mirror;
}
